import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { IMAGES } from '@/../images';
import { Calendar, Sun, Moon, ArrowRight, Loader2, AlertCircle, ChevronLeft, HelpCircle } from 'lucide-react';

interface StreakRecoveryPageProps {
  onBack: () => void;
  onOpenTicket: () => void;
}

type Edition = 'morning' | 'night';

export function StreakRecoveryPage({ onBack, onOpenTicket }: StreakRecoveryPageProps) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedEdition, setSelectedEdition] = useState<Edition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quizNotFound, setQuizNotFound] = useState(false);

  // Usa hora local do navegador (Brasil) para calcular limites de data
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const maxDate = toLocalDateStr(today);

  // Limitar a 7 dias atrás
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 7);
  const minDateStr = toLocalDateStr(minDate);

  const formatDateBR = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleFetchQuiz = async () => {
    if (!selectedDate || !selectedEdition) return;

    setLoading(true);
    setError('');
    setQuizNotFound(false);

    try {
      const response = await fetch(
        `https://worker.thenewscc.com/quiz/by-date?date=${selectedDate}&edition=${selectedEdition}`
      );
      const data = await response.json();

      if (data.success && data.data?.quiz?.id) {
        window.location.href = `https://thenewscc.com.br/quiz/${data.data.quiz.id}`;
      } else {
        setQuizNotFound(true);
      }
    } catch {
      setError('Não foi possível buscar o quiz. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = selectedDate && selectedEdition && !loading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8 px-4">
      <div className="w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center"
        >
          <img
            src={IMAGES.newLogos.theNews}
            alt="the news"
            className="w-40 mx-auto mb-8"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100 w-full"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-r from-[#ffce04] to-[#ff8a47] mb-4">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                recupere seu streak
              </h2>
              <p className="text-gray-500 text-base max-w-md mx-auto">
                selecione o dia que você perdeu e o tipo da edição. você será
                redirecionado para um quiz — acerte{' '}
                <span className="font-semibold text-gray-700">6 de 10</span>{' '}
                questões para recuperar.
              </p>
            </div>

            {/* Date Picker */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  qual dia você perdeu o streak?
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setError('');
                      setQuizNotFound(false);
                    }}
                    min={minDateStr}
                    max={maxDate}
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all outline-none appearance-none"
                  />
                </div>
                {selectedDate && (
                  <p className="text-xs text-gray-400 mt-1.5">
                    {formatDateBR(selectedDate)}
                  </p>
                )}
              </div>

              {/* Edition Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  qual edição?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEdition('morning');
                      setError('');
                      setQuizNotFound(false);
                    }}
                    className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      selectedEdition === 'morning'
                        ? 'border-[#ffce04] bg-[#ffce04]/5 shadow-sm'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <Sun className={`w-6 h-6 ${selectedEdition === 'morning' ? 'text-[#ff8a47]' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${selectedEdition === 'morning' ? 'text-gray-900' : 'text-gray-600'}`}>
                      diurna
                    </span>
                    {selectedEdition === 'morning' && (
                      <motion.div
                        layoutId="edition-check"
                        className="absolute top-2 right-2 w-5 h-5 rounded-full bg-gradient-to-r from-[#ffce04] to-[#ff8a47] flex items-center justify-center"
                      >
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </motion.div>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEdition('night');
                      setError('');
                      setQuizNotFound(false);
                    }}
                    className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      selectedEdition === 'night'
                        ? 'border-[#ffce04] bg-[#ffce04]/5 shadow-sm'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <Moon className={`w-6 h-6 ${selectedEdition === 'night' ? 'text-[#ff8a47]' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${selectedEdition === 'night' ? 'text-gray-900' : 'text-gray-600'}`}>
                      noturna
                    </span>
                    {selectedEdition === 'night' && (
                      <motion.div
                        layoutId="edition-check"
                        className="absolute top-2 right-2 w-5 h-5 rounded-full bg-gradient-to-r from-[#ffce04] to-[#ff8a47] flex items-center justify-center"
                      >
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </motion.div>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Error / Not Found States */}
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              {quizNotFound && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm"
                >
                  <p className="text-amber-700 font-medium mb-1">
                    quiz não encontrado para esta data e edição.
                  </p>
                  <p className="text-amber-600">
                    verifique se a data e edição estão corretos ou{' '}
                    <button
                      onClick={onOpenTicket}
                      className="underline font-medium hover:text-amber-800 transition-colors"
                    >
                      abra um ticket para o suporte
                    </button>.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* CTA Button */}
            <div className="mt-6">
              <Button
                onClick={handleFetchQuiz}
                disabled={!canProceed}
                className="w-full bg-gradient-to-r from-[#ffce04] to-[#ff8a47] hover:opacity-90 text-white py-6 text-lg rounded-xl transition-opacity disabled:opacity-40"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    ir para o quiz
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                voltar
              </button>

              <button
                onClick={onOpenTicket}
                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                prefiro abrir um ticket
              </button>
            </div>
          </motion.div>

          {/* Info pill */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xs text-gray-400 mt-4 text-center"
          >
            o quiz é baseado na edição do dia selecionado
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
